// ─── Multi-account store ──────────────────────────────────────────────────────
// Manages up to 5 simultaneous JMAP accounts (like Gmail's account switcher).
// Credentials (passwords) live in memory only — never persisted to localStorage.
// JWTs and emails are persisted so the app can re-hydrate on page reload.

import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { JMAPClient } from '../lib/jmap'
import type { JMAPSession, JMAPMailbox, JMAPStateChange } from '../lib/jmapTypes'
import type { User } from '../lib/types'

export const MAX_ACCOUNTS = 5
const STORAGE_KEY = 'tmail_accounts'

export interface StoredAccount {
  email: string
  accessToken: string
  refreshToken: string  // persisted so secondary accounts survive page reloads
  jmapUrl: string
  // Persisted so role-gated UI (e.g. Control Panel button) works immediately on
  // reload, even before the JMAP client reconnects and /auth/me is re-fetched.
  cachedRole?: string
  cachedName?: string
}

export interface AccountEntry {
  email: string
  accessToken: string   // TMail backend JWT
  refreshToken: string  // Per-account refresh token
  jmapUrl: string
  // Persisted across reloads as a lightweight fallback before full reconnect:
  cachedRole?: string
  cachedName?: string
  // In-memory only (never persisted):
  user: User | null
  client: JMAPClient | null
  session: JMAPSession | null
  mailboxes: JMAPMailbox[]
  pushConnected: boolean
  isLoading: boolean
  error: string | null
}

interface AccountStore {
  accounts: AccountEntry[]
  activeEmail: string | null

  // Actions
  addAccount(email: string, accessToken: string, refreshToken: string, jmapUrl: string, password: string, user: User): Promise<void>
  removeAccount(email: string): void
  setActiveAccount(email: string): void
  updateAccount(email: string, patch: Partial<AccountEntry>): void

  // Called when JMAP push delivers a state change
  onStateChange(email: string, change: JMAPStateChange): void

  // Getters
  getActiveAccount(): AccountEntry | null
  getActiveClient(): JMAPClient | null
  getActiveMailboxes(): JMAPMailbox[]
  getActiveRefreshToken(): string

  // Restore from localStorage on app boot
  getPersistedAccounts(): StoredAccount[]
}

// Helpers
function persistAccounts(accounts: AccountEntry[]) {
  const stored: StoredAccount[] = accounts.map(a => ({
    email: a.email,
    accessToken: a.accessToken,
    refreshToken: a.refreshToken,
    jmapUrl: a.jmapUrl,
    // Persist role + name so role-gated UI (e.g. CP button) works immediately
    // on the next reload before the JMAP client reconnects.
    cachedRole: a.user?.role ?? a.cachedRole,
    cachedName: a.user?.name ?? a.cachedName,
  }))
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
}

// Pre-populate accounts from localStorage on store creation (no JMAP clients yet —
// they will be reconnected by App.tsx after credentials are fetched from the API).
const initialAccounts: AccountEntry[] = (() => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    return (JSON.parse(raw) as StoredAccount[]).map((s): AccountEntry => ({
      email: s.email,
      accessToken: s.accessToken,
      refreshToken: s.refreshToken ?? '',
      jmapUrl: s.jmapUrl,
      cachedRole: s.cachedRole,
      cachedName: s.cachedName,
      user: null, client: null, session: null,
      mailboxes: [], pushConnected: false,
      isLoading: false, error: null,
    }))
  } catch { return [] }
})()

export const useAccountStore = create<AccountStore>()(
  subscribeWithSelector((set, get) => ({
    accounts: initialAccounts,
    activeEmail: null, // set explicitly after primary account connects

    async addAccount(email, accessToken, refreshToken, jmapUrl, password, user) {
      const existing = get().accounts.find(a => a.email === email)
      if (existing) {
        // Disconnect old push before reconnecting (prevent duplicate EventSource)
        existing.client?.disconnectPush()
        // Update metadata and reconnect JMAP client (token/url may have changed)
        set(state => ({
          accounts: state.accounts.map(a =>
            a.email === email
              ? { ...a, accessToken, refreshToken, jmapUrl, user,
                  cachedRole: user?.role ?? a.cachedRole,
                  cachedName: user?.name ?? a.cachedName,
                  isLoading: true, error: null }
              : a
          ),
        }))
      } else {
        if (get().accounts.length >= MAX_ACCOUNTS) {
          throw new Error(`Maximum ${MAX_ACCOUNTS} accounts supported`)
        }
        set(state => ({
          accounts: [...state.accounts, {
            email, accessToken, refreshToken, jmapUrl,
            user, cachedRole: user?.role, cachedName: user?.name,
            client: null, session: null,
            mailboxes: [], pushConnected: false,
            isLoading: true, error: null,
          }],
        }))
      }
      persistAccounts(get().accounts)

      // Admin/superadmin accounts have no Stalwart mailbox — skip JMAP entirely.
      // Also skip if credentials are missing (no jmapUrl or no password).
      const isAdminRole = user?.role === 'admin' || user?.role === 'superadmin'
      if (isAdminRole || !password || !jmapUrl) {
        set(state => ({
          accounts: state.accounts.map(a =>
            a.email === email ? { ...a, isLoading: false } : a
          ),
        }))
        return
      }

      // Connect (or reconnect) JMAP client
      try {
        const client = new JMAPClient(jmapUrl, email, password)
        const session = await client.connect()
        const mailboxes = await client.listMailboxes()

        set(state => ({
          accounts: state.accounts.map(a =>
            a.email === email
              ? { ...a, client, session, mailboxes, isLoading: false }
              : a
          ),
        }))
      } catch (err) {
        set(state => ({
          accounts: state.accounts.map(a =>
            a.email === email
              ? { ...a, isLoading: false, error: String(err) }
              : a
          ),
        }))
      }
    },

    removeAccount(email) {
      const { accounts, activeEmail } = get()
      const account = accounts.find(a => a.email === email)
      account?.client?.disconnectPush()

      const next = accounts.filter(a => a.email !== email)
      const nextActive = activeEmail === email
        ? (next[0]?.email ?? null)
        : activeEmail
      set({ accounts: next, activeEmail: nextActive })
      persistAccounts(next)
    },

    setActiveAccount(email) {
      set({ activeEmail: email })
    },

    updateAccount(email, patch) {
      set(state => ({
        accounts: state.accounts.map(a => a.email === email ? { ...a, ...patch } : a),
      }))
      // Persist so token/role changes survive page reload
      persistAccounts(get().accounts)
    },

    onStateChange(email, change) {
      // Bubble up via a custom event so App.tsx can trigger a re-fetch
      window.dispatchEvent(new CustomEvent('jmap:statechange', { detail: { email, change } }))
    },

    getActiveAccount() {
      const { accounts, activeEmail } = get()
      return accounts.find(a => a.email === activeEmail) ?? null
    },

    getActiveClient() {
      return get().getActiveAccount()?.client ?? null
    },

    getActiveMailboxes() {
      return get().getActiveAccount()?.mailboxes ?? []
    },

    getActiveRefreshToken() {
      return get().getActiveAccount()?.refreshToken ?? ''
    },

    getPersistedAccounts(): StoredAccount[] {
      try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return []
        return JSON.parse(raw) as StoredAccount[]
      } catch {
        return []
      }
    },
  }))
)
